'use client'

import {useId, useLayoutEffect, useState} from 'react'
import {createPortal} from 'react-dom'

const SPOTLIGHT_PAD = 10
const CARD_WIDTH = 300
const VIEWPORT_MARGIN = 16

const DIM_FILL = 'rgba(15, 23, 42, 0.52)'

type Hole = {x: number; y: number; w: number; h: number; r: number}

function measureHole(el: HTMLElement): Hole {
  const rect = el.getBoundingClientRect()
  const w = rect.width + SPOTLIGHT_PAD * 2
  const h = rect.height + SPOTLIGHT_PAD * 2
  const r = Math.min(w, h) / 2
  return {
    x: rect.left - SPOTLIGHT_PAD,
    y: rect.top - SPOTLIGHT_PAD,
    w,
    h,
    r,
  }
}

function CircMaskBackdrop({vw, vh, cx, cy, radius, maskId}: {vw: number; vh: number; cx: number; cy: number; radius: number; maskId: string}) {
  return (
    <svg aria-hidden className='pointer-events-none fixed inset-0 z-[9998]' width={vw} height={vh} role='presentation'>
      <defs>
        <mask id={maskId} maskUnits='userSpaceOnUse'>
          <rect width={vw} height={vh} fill='white' />
          <circle cx={cx} cy={cy} r={radius} fill='black' />
        </mask>
      </defs>
      <rect width={vw} height={vh} fill={DIM_FILL} mask={`url(#${maskId})`} />
    </svg>
  )
}

type Props = {
  measureRef: React.RefObject<HTMLElement | null>
  open: boolean
  onDismiss: () => void
}

export function PortalViewSwitcherOnboarding({measureRef, open, onDismiss}: Props) {
  const [, bump] = useState(0)
  const rawMaskId = useId()
  const maskId = rawMaskId.replace(/:/g, '')

  useLayoutEffect(() => {
    if (open) bump((n) => n + 1)
  }, [open])

  useLayoutEffect(() => {
    if (!open) return

    let scheduled = false
    const scheduleBump = () => {
      if (scheduled) return
      scheduled = true
      requestAnimationFrame(() => {
        scheduled = false
        bump((n) => n + 1)
      })
    }

    const onWin = () => scheduleBump()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)

    let anchorRo: ResizeObserver | undefined
    const anchor = measureRef.current
    if (anchor && typeof ResizeObserver !== 'undefined') {
      anchorRo = new ResizeObserver(onWin)
      anchorRo.observe(anchor)
    }

    const header = anchor?.closest('[data-slot="app-header"]')
    let mo: MutationObserver | undefined
    let headerRo: ResizeObserver | undefined
    if (header) {
      if (typeof MutationObserver !== 'undefined') {
        mo = new MutationObserver(() => scheduleBump())
        mo.observe(header, {childList: true, subtree: true})
      }
      if (typeof ResizeObserver !== 'undefined') {
        headerRo = new ResizeObserver(() => scheduleBump())
        headerRo.observe(header)
      }
    }

    let rafId = 0
    let frames = 0
    const MAX_FRAMES = 150
    let lastKey = ''
    const sampleLayout = () => {
      const el = measureRef.current
      if (el) {
        const r = el.getBoundingClientRect()
        const key = `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)}`
        if (key !== lastKey) {
          lastKey = key
          scheduleBump()
        }
      }
      frames += 1
      if (frames < MAX_FRAMES) {
        rafId = requestAnimationFrame(sampleLayout)
      }
    }
    rafId = requestAnimationFrame(sampleLayout)

    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
      anchorRo?.disconnect()
      mo?.disconnect()
      headerRo?.disconnect()
      cancelAnimationFrame(rafId)
    }
  }, [open, measureRef])

  useLayoutEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const el = measureRef.current

  if (typeof window === 'undefined' || !open || !el) return null

  const vw = window.innerWidth
  const vh = window.innerHeight
  const measured = measureHole(el as HTMLElement)

  const cx = measured.x + measured.w / 2
  const cy = measured.y + measured.h / 2
  const spotlightR = measured.r

  let cardLeft = cx - CARD_WIDTH / 2
  cardLeft = Math.max(VIEWPORT_MARGIN, Math.min(cardLeft, vw - CARD_WIDTH - VIEWPORT_MARGIN))

  const arrowLeftPx = cx - cardLeft

  let cardTop = measured.y + measured.h + VIEWPORT_MARGIN
  const cardEstimatedHeight = 210
  const belowFits = cardTop + cardEstimatedHeight <= vh - VIEWPORT_MARGIN
  let cardIsBelowHole = belowFits
  if (!belowFits) {
    cardTop = Math.max(VIEWPORT_MARGIN, measured.y - cardEstimatedHeight - VIEWPORT_MARGIN)
    cardIsBelowHole = false
  }

  const ringDiameter = spotlightR * 2

  return createPortal(
    <div data-slot='portal-view-switcher-onboarding' aria-modal='true' role='dialog'>
      <CircMaskBackdrop vw={vw} vh={vh} cx={cx} cy={cy} radius={spotlightR} maskId={maskId} />

      <div
        aria-hidden
        className='pointer-events-none fixed z-[9999]'
        style={{
          left: cx - spotlightR,
          top: cy - spotlightR,
          width: ringDiameter,
          height: ringDiameter,
          borderRadius: '50%',
          boxShadow: '0 0 0 1.5px rgba(255,255,255,0.95), 0 0 0 10px rgba(255,255,255,0.12), 0 24px 56px -10px rgba(15,23,42,0.4)',
        }}
      />

      <div
        data-slot='portal-view-switcher-onboarding-card'
        className='pointer-events-none fixed z-[10001]'
        style={{left: cardLeft, top: cardTop, width: CARD_WIDTH}}>
        {cardIsBelowHole ? (
          <div
            aria-hidden
            className='pointer-events-none absolute -top-[9px] z-[1] flex h-[10px] w-[22px] items-end justify-center'
            style={{left: arrowLeftPx, transform: 'translateX(-50%)'}}>
            <svg width='22' height='10' viewBox='0 0 22 10' fill='none' aria-hidden>
              <path d='M11 1.75L2.6 9.25h16.8L11 1.75z' fill='white' stroke='#E8ECF2' strokeWidth='1' strokeLinejoin='round' />
            </svg>
          </div>
        ) : (
          <div
            aria-hidden
            className='pointer-events-none absolute -bottom-[9px] z-[1] flex h-[10px] w-[22px] items-start justify-center'
            style={{left: arrowLeftPx, transform: 'translateX(-50%)'}}>
            <svg width='22' height='10' viewBox='0 0 22 10' fill='none' aria-hidden className='rotate-180'>
              <path d='M11 1.75L2.6 9.25h16.8L11 1.75z' fill='white' stroke='#E8ECF2' strokeWidth='1' strokeLinejoin='round' />
            </svg>
          </div>
        )}

        <div className='pointer-events-auto relative rounded-2xl border border-[#E8ECF2] bg-white p-[18px] shadow-[0_12px_32px_-6px_rgba(16,24,40,0.16),0_4px_12px_-2px_rgba(16,24,40,0.08)]'>
          <h2 className='text-base font-bold text-[#102A56]'>Novidade!</h2>
          <p className='mt-2 text-sm leading-5 text-[#475467]'>Agora você pode escolher em quais módulos deseja utilizar a nova interface.</p>
          <div className='mt-4 flex justify-end'>
            <button
              type='button'
              data-slot='portal-view-switcher-onboarding-dismiss'
              className='h-9 min-w-[104px] rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50'
              onClick={onDismiss}>
              Entendi!
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
